/**
 * T483 — inline preview of the primary expense attachment.
 *
 * Picks a renderer by mime-type:
 *   - PDF → rendered canvas pages to the `/preview` endpoint (Content-Disposition: inline)
 *   - image/* → `<img>` to the same endpoint
 *   - everything else → fallback to a download link
 *
 * Lives above the existing attachments list. Auditor / Stbr can now read
 * the source PDF without leaving the detail page (FR-021 parity with
 * Durcharbeitemodus slideshow).
 *
 * T491 also reuses this component as the prominent top-of-page element.
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import { LoadingSpinner } from '../ui';
import { api } from '../../services/api';
import type { ExpenseAttachmentDto } from '@armard/shared';

const ExpensePdfPreview = lazy(() => import('./ExpensePdfPreview'));

export interface ExpenseAttachmentPreviewProps {
  /** Primary attachment to preview. If `null`, renders the empty-state. */
  attachment: ExpenseAttachmentDto | null;
  /** Vertical height — defaults to a comfortable detail-page panel. */
  heightClass?: string;
  testid?: string;
}

function isPdf(mime: string): boolean {
  return mime === 'application/pdf';
}
function isImage(mime: string): boolean {
  return mime.startsWith('image/');
}

export function ExpenseAttachmentPreview(props: ExpenseAttachmentPreviewProps) {
  const { attachment, heightClass = 'h-[60vh]', testid = 'expense-attachment-preview' } = props;
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [attachment?.id]);

  if (!attachment) {
    return (
      <div
        className={`flex items-center justify-center bg-white/5 border border-white/10 rounded-lg ${heightClass}`}
        data-testid={`${testid}-empty`}
      >
        <p className="text-white/40 text-sm">Kein Beleg angehängt</p>
      </div>
    );
  }

  // The download path resolves against `api.baseUrl` so the iframe carries
  // the session cookie. Canvas PDF requests explicitly include credentials;
  // images use the same authenticated attachment endpoint.
  const previewUrl = `${api.baseUrl}/expense-attachments/${attachment.id}/preview`;

  if (isPdf(attachment.mimeType)) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <ExpensePdfPreview
          key={attachment.id}
          url={previewUrl}
          fileName={attachment.fileNameOriginal}
          heightClass={heightClass}
          testid={testid}
        />
      </Suspense>
    );
  }

  if (isImage(attachment.mimeType) && !failed) {
    return (
      <div
        className={`flex items-center justify-center bg-white/5 border border-white/10 rounded-lg overflow-hidden ${heightClass}`}
        data-testid={testid}
      >
        <img
          src={previewUrl}
          onError={() => setFailed(true)}
          alt={attachment.fileNameOriginal}
          className="max-w-full max-h-full object-contain"
          data-testid={`${testid}-img`}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 bg-white/5 border border-white/10 rounded-lg ${heightClass}`}
      data-testid={`${testid}-fallback`}
    >
      <p className="text-white/50 text-sm">
        {failed
          ? 'Vorschau konnte nicht geladen werden. Bitte das Original herunterladen.'
          : `Vorschau für ${attachment.mimeType} nicht verfügbar`}
      </p>
      <a
        href={previewUrl}
        download={attachment.fileNameOriginal}
        className="text-sky-400 hover:text-sky-300 text-sm underline"
      >
        ⬇ {attachment.fileNameOriginal}
      </a>
    </div>
  );
}
